import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaFileInvoiceDollar } from 'react-icons/fa';
import * as clientService from '../../services/client.service';
import * as clientCatalogService from '../../services/clientCatalog.service';
import * as companyService from '../../services/company.service';
import * as proformaService from '../../services/proformaInvoice.service';
import { computeBoxCbmFromDims } from '../../components/TearSheet/tearSheetUtils';
import ProformaInvoicePrint from './ProformaInvoicePrint';
import PageHeader from '../../components/PageHeader/PageHeader';
import './ProformaInvoice.css';

const packSummary = (packs) => {
  if (!Array.isArray(packs) || packs.length === 0) return '—';
  const parts = packs
    .map((row) => row?.quantityPack ?? row?.quantity_pack)
    .filter((v) => v !== undefined && v !== null && v !== '');
  return parts.length > 0 ? parts.join(' / ') : '—';
};

/** Comma-joined color names from a catalog line's color array. */
const colorSummary = (colors) => {
  if (!Array.isArray(colors) || colors.length === 0) return '';
  return colors
    .map((c) => (typeof c === 'string' ? c : c?.name))
    .filter(Boolean)
    .join(', ');
};

/** Dimension string in the tear sheet format, e.g. "27.00x28.00x24.00 CM". */
const formatDimension = (length, breadth, height, unit) => {
  const vals = [length, breadth, height].map((v) => {
    const n = parseFloat(String(v ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n.toFixed(2) : null;
  });
  if (vals.some((v) => v === null)) return '';
  return `${vals[0]}x${vals[1]}x${vals[2]} ${String(unit || '').toUpperCase()}`.trim();
};

/** Total CBM from the tear sheet = sum of each master pack's Box CBM. Used as CBM per carton. */
const cbmPerCarton = (masterPacks) => {
  return (Array.isArray(masterPacks) ? masterPacks : []).reduce(
    (sum, row) => sum + (computeBoxCbmFromDims(row.length, row.breadth, row.height) ?? 0),
    0,
  );
};

const ProformaInvoice = () => {
  const [branches, setBranches] = useState([]);
  const [clients, setClients] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [clientId, setClientId] = useState('');

  const [rows, setRows] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [invoice, setInvoice] = useState(null);

  // Load exporter branches + clients once.
  useEffect(() => {
    const load = async () => {
      try {
        const [company, clientRes] = await Promise.all([
          companyService.getCompanyDetails(),
          clientService.getClientList(1, 1000),
        ]);
        setBranches(Array.isArray(company?.branches) ? company.branches : []);
        setClients(Array.isArray(clientRes?.data) ? clientRes.data : []);
      } catch (e) {
        console.error('Failed to load proforma prerequisites', e);
      }
    };
    load();
  }, []);

  // Load the selected client's APPROVED catalog products as editable line rows.
  const loadApprovedItems = useCallback(async (code) => {
    if (!code) {
      setRows([]);
      return;
    }
    setLoadingItems(true);
    setError(null);
    try {
      const catalog = await clientCatalogService.getClientCatalog(code);
      const approved = (catalog || [])
        .filter((item) => (item?.tearSheetStatus || '').toLowerCase() === 'approved')
        .map((item) => {
          const masterPacks = Array.isArray(item.masterPacks) ? item.masterPacks : [];
          const moq = Number(item.moq) > 0 ? Number(item.moq) : 0;
          return {
            productUid: item.uid,
            pCode: item.pCode || '—',
            itemNo: item.itemNo || '—',
            image: item.images?.[0]?.path || '',
            description: item.productName || item.itemNo || '—',
            color: colorSummary(item.color),
            dimension: formatDimension(item.length, item.breadth, item.height, item.dimensionUnit),
            barcode: item.barcodeType?.name || '',
            innerPack: packSummary(item.innerPacks),
            masterPack: packSummary(item.masterPacks),
            clientSku: item.skuId || '—',
            price: item.tearSheetCost ?? item.cost ?? 0,
            currency: item.currency?.name || '',
            version: item.version || 1,
            moq,
            cbmPerCarton: cbmPerCarton(masterPacks),
            cartons: '',
            orderQty: moq > 0 ? String(moq) : '',
          };
        });
      setRows(approved);
    } catch (e) {
      console.error('Failed to load approved items', e);
      setError('Failed to load approved products for this client.');
      setRows([]);
    } finally {
      setLoadingItems(false);
    }
  }, []);

  useEffect(() => {
    loadApprovedItems(clientId);
  }, [clientId, loadApprovedItems]);

  const handleQtyChange = (uid, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.productUid === uid ? { ...row, [field]: value } : row)),
    );
  };

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        const cartons = Number(row.cartons || 0);
        const orderQty = Number(row.orderQty || 0);
        const totalQty = cartons * orderQty;
        acc.cartons += cartons;
        acc.qty += totalQty;
        acc.amount += totalQty * Number(row.price || 0);
        acc.cbm += cartons * Number(row.cbmPerCarton || 0);
        return acc;
      },
      { cartons: 0, qty: 0, amount: 0, cbm: 0 },
    );
  }, [rows]);

  // Currency configured for the selected client in the Buyer Ledger (shown alongside prices).
  const currencyLabel = useMemo(() => {
    const selected = clients.find(
      (c) => String(c.clientId || c.client_id) === String(clientId),
    );
    return selected?.currency?.symbol || selected?.currency?.name || '';
  }, [clients, clientId]);

  const money = (value) => {
    const formatted = Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return currencyLabel ? `${currencyLabel} ${formatted}` : formatted;
  };

  const canGenerate = branchId && clientId && rows.length > 0 && !generating;

  const handleGenerate = async () => {
    if (!canGenerate) return;

    // Enforce MOQ: order qty for each line must be at least the product's MOQ.
    const belowMoq = rows.find(
      (row) => row.moq > 0 && Number(row.orderQty || 0) > 0 && Number(row.orderQty) < row.moq,
    );
    if (belowMoq) {
      setError(`Order Qty for ${belowMoq.pCode} must be at least its MOQ (${belowMoq.moq}).`);
      return;
    }

    setGenerating(true);
    setError(null);
    try {
      const payload = {
        branch_id: Number(branchId),
        client_id: clientId,
        items: rows.map((row) => ({
          productUid: row.productUid,
          cartons: Number(row.cartons || 0),
          orderQty: Number(row.orderQty || 0),
        })),
      };
      const result = await proformaService.generateProformaInvoice(payload);
      setInvoice(result);
    } catch (e) {
      const apiErr =
        e.response?.data?.errors?.items?.[0] ||
        e.response?.data?.message ||
        e.message ||
        'Failed to generate proforma invoice.';
      setError(apiErr);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="page-shell">
      <PageHeader
        icon={<FaFileInvoiceDollar size={22} />}
        title="Proforma Invoice"
        subtitle="Select the company and buyer, set quantities for approved products, and generate a printable invoice."
      />
      <div className="proforma-page">

      <div className="proforma-card">
        <div className="proforma-selectors">
          <div className="proforma-field">
            <label htmlFor="pf-branch">Company Name</label>
            <select
              id="pf-branch"
              className="form-select"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              <option value="">Select company…</option>
              {branches.map((b) => (
                <option key={b.id ?? b.localKey} value={b.id}>
                  {b.label}{b.companyName ? ` — ${b.companyName}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="proforma-field">
            <label htmlFor="pf-client">Buyer (Client)</label>
            <select
              id="pf-client"
              className="form-select"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            >
              <option value="">Select buyer…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.clientId || c.client_id}>
                  {c.name} ({c.clientId || c.client_id})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="proforma-card">
        <h5 className="mb-3">Approved products</h5>

        {error && <div className="alert alert-danger py-2 px-3">{error}</div>}

        {!clientId ? (
          <div className="proforma-empty">Select a buyer to load their approved products.</div>
        ) : loadingItems ? (
          <div className="proforma-empty">Loading approved products…</div>
        ) : rows.length === 0 ? (
          <div className="proforma-empty">This buyer has no approved products to invoice.</div>
        ) : (
          <div className="table-responsive">
            <table className="proforma-items-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>P code</th>
                  <th>Image</th>
                  <th>Description</th>
                  <th>Barcode</th>
                  <th>Inner Qty</th>
                  <th>Master Qty</th>
                  <th>Client SKU</th>
                  <th>Price</th>
                  <th>Cartons</th>
                  <th>CBM/Carton</th>
                  <th>Total CBM</th>
                  <th>Order Qty</th>
                  <th>Total Qty</th>
                  <th>Total Price</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const cartons = Number(row.cartons || 0);
                  const totalQty = cartons * Number(row.orderQty || 0);
                  const totalPrice = totalQty * Number(row.price || 0);
                  const totalCbm = cartons * Number(row.cbmPerCarton || 0);
                  return (
                    <tr key={row.productUid}>
                      <td>{index + 1}</td>
                      <td>{row.pCode}</td>
                      <td>
                        {row.image ? (
                          <img src={row.image} alt={row.itemNo} className="proforma-thumb" />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="pf-desc-cell">
                        <div className="pf-desc-name">{row.description}</div>
                        {row.color && <div className="pf-desc-sub">Color: {row.color}</div>}
                        {row.dimension && <div className="pf-desc-sub">{row.dimension}</div>}
                      </td>
                      <td>{row.barcode || '—'}</td>
                      <td>{row.innerPack}</td>
                      <td>{row.masterPack}</td>
                      <td>{row.clientSku}</td>
                      <td>{money(row.price)}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          className="pf-qty-input"
                          value={row.cartons}
                          onChange={(e) => handleQtyChange(row.productUid, 'cartons', e.target.value)}
                        />
                      </td>
                      <td>{Number(row.cbmPerCarton || 0).toFixed(4)}</td>
                      <td>{totalCbm.toFixed(4)}</td>
                      <td>
                        <input
                          type="number"
                          min={row.moq || 0}
                          className="pf-qty-input"
                          value={row.orderQty}
                          title={row.moq ? `Minimum order qty (MOQ): ${row.moq}` : undefined}
                          onChange={(e) => handleQtyChange(row.productUid, 'orderQty', e.target.value)}
                        />
                      </td>
                      <td>{totalQty.toLocaleString()}</td>
                      <td>{money(totalPrice)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={9} className="text-end fw-bold">Total</td>
                  <td className="fw-bold">{totals.cartons.toLocaleString()}</td>
                  <td></td>
                  <td className="fw-bold">{totals.cbm.toFixed(4)}</td>
                  <td></td>
                  <td className="fw-bold">{totals.qty.toLocaleString()}</td>
                  <td className="fw-bold">{money(totals.amount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="proforma-generate-bar">
        {rows.length > 0 && (
          <span className="text-muted">
            {totals.cartons.toLocaleString()} cartons · {totals.cbm.toFixed(4)} CBM · Estimated total: {money(totals.amount)}
          </span>
        )}
        <button type="button" className="btn btn-primary" disabled={!canGenerate} onClick={handleGenerate}>
          {generating ? 'Generating…' : 'Generate Proforma Invoice'}
        </button>
      </div>

      {invoice && <ProformaInvoicePrint invoice={invoice} onClose={() => setInvoice(null)} />}
      </div>
    </div>
  );
};

export default ProformaInvoice;
